<template>
  <main class="container">
    <section id="configurator">
      <header class="header">
        <p class="title">Simulation tarifaire</p>
        <p class="subtitle">Estimez votre offre Mashup Web Social</p>

        <!-- Period selector -->
        <PeriodSelector
          :selected-period="pricingState.period"
          @period-change="onPeriodChange"
        />
      </header>

      <!-- Slider controls -->
      <div class="sliders-container">
        <ControlSlider
          v-for="control in sliderControls"
          :key="control.key"
          :label="control.label"
          :icon="control.icon"
          :value="pricingState[control.key]"
          :scale-name="control.scale"
          :unique-id="control.key"
          @input="onValueChange(control.key, $event)"
        />
      </div>
    </section>

    <!-- Result card -->
    <ResultCard :result="selectedPlan" :environment="environment" />
  </main>
</template>

<script>
import { usePricingAdvanced } from "@composables/usePricingAdvanced"
import { usePricingBasic } from "@composables/usePricingBasic"
import {
  defaultConfig,
  INITIAL_VALUES,
  PERIODS,
  PRICING_MODES,
  ENVIRONMENTS,
} from "@config/pricingConfig"
import ControlSlider from "./ControlSlider.vue"
import PeriodSelector from "./PeriodSelector.vue"
import ResultCard from "./ResultCard.vue"

export default {
  name: "PricingCalculator",

  components: {
    PeriodSelector,
    ControlSlider,
    ResultCard,
  },

  props: {
    // Allow switching logic mode via props
    mode: {
      type: String,
      default: defaultConfig.mode,
      validator: (value) => Object.values(PRICING_MODES).includes(value),
    },
    // Allow switching environment (chat platform)
    environment: {
      type: String,
      default: defaultConfig.environment,
      validator: (value) => Object.values(ENVIRONMENTS).includes(value),
    },
  },

  data() {
    return {
      pricingState: {
        period: PERIODS.ONE_YEAR, // Default period
        socialAccounts: 10, // Will be adjusted in mounted()
        users: 10,
        sources: 10,
      },
      sliderControls: [
        {
          key: "socialAccounts",
          label: "Nombre de comptes sociaux (pages)",
          icon: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMTEuNzUiIGN5PSIxMS43NSIgcj0iMTEiIHN0cm9rZT0iIzU1NUU3NSIgc3Ryb2tlLXdpZHRoPSIxLjUiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPgo8ZWxsaXBzZSBjeD0iMTEuNzUiIGN5PSIxMS43NSIgcng9IjUuNSIgcnk9IjExIiBzdHJva2U9IiM1NTVFNzUiIHN0cm9rZS13aWR0aD0iMS41IiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz4KPHBhdGggZD0iTTIxLjgzMyA3LjE2NjY3IEwxLjY2NjM0IDcuMTY2NjciIHN0cm9rZT0iIzU1NUU3NSIgc3Ryb2tlLXdpZHRoPSIxLjUiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz4KPHBhdGggZD0iTTIxLjgzMyAxNi4zMzMyIEwxLjY2NjM0IDE2LjMzMzIiIHN0cm9rZT0iIzU1NUU3NSIgc3Ryb2tlLXdpZHRoPSIxLjUiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz4KPC9zdmc+',
          scale: "social",
        },
        {
          key: "users",
          label: "Nombre d'utilisateurs",
          icon: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjAiIHZpZXdCb3g9IjAgMCAyNCAyMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTE4LjY4MTUgMTguMDM1NkgxOS4xOTVDMjAuMzk5NiAxOC4wMzU2IDIxLjM1NzcgMTcuNDg2NyAyMi4yMTggMTYuNzE5M0MyNC40MDM1IDE0Ljc2OTcgMTkuMjY2IDEyLjc5NzUgMTcuNTEyMiAxMi43OTc1TTE1LjQxNyAyLjM5MzMzQzE1LjY1NDkgMi4zNDYxNSAxNS45MDE5IDIuMzIxMjkgMTYuMTU1NCAyLjMyMTI5QzE4LjA2MTkgMi4zMjEyOSAxOS42MDc1IDMuNzI4NCAxOS42MDc1IDUuNDY0MTRDMTkuNjA3NSA3LjE5OTg5IDE4LjA2MTkgOC42MDcgMTYuMTU1NCA4LjYwN0MxNS45MDE5IDguNjA3IDE1LjY1NDkgOC41ODIxNyAxNS40MTcgOC41MzQ5MiIgc3Ryb2tlPSIjNTU1RTc1IiBzdHJva2Utd2lkdGg9IjEuNSIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIi8+CjxwYXRoIGQ9Ik0zLjg3MzI4IDEzLjk2MTVDMi42MzgxNyAxNC42MjM0IC0wLjYwMDIzNCAxNS45NzQ5IDEuMzcyMTYgMTcuNjY2MUMyLjMzNTY3IDE4LjQ5MjIgMy40MDg3NiAxOS4wODMxIDQuNzU3ODkgMTkuMDgzMUgxMi40NTY0QzEzLjgwNTUgMTkuMDgzMSAxNC44Nzg2IDE4LjQ5MjIgMTUuODQyMSAxNy42NjYxQzE3LjgxNDUgMTUuOTc0OSAxNC41NzYxIDE0LjYyMzQgMTMuMzQxIDEzLjk2MTVDMTAuNDQ0NyAxMi40MDkzIDYuNzY5NiAxMi40MDkzIDMuODczMjggMTMuOTYxNVoiIHN0cm9rZT0iIzU1NUU3NSIgc3Ryb2tlLXdpZHRoPSIxLjUiLz4KPHBhdGggZD0iTTEyLjc5NzkgNC45NDA0N0MxMi43OTc5IDcuMjU0ODEgMTAuOTIxOCA5LjEzMDk0IDguNjA3NDYgOS4xMzA5NEM2LjI5MzEzIDkuMTMwOTQgNC40MTY5OSA3LjI1NDgxIDQuNDE2OTkgNC45NDA0N0M0LjQxNjk5IDIuNjI2MTQgNi4yOTMxMyAwLjc1IDguNjA3NDYgMC43NUMxMC45MjE4IDAuNzUgMTIuNzk3OSAyLjYyNjE0IDEyLjc5NzkgNC45NDA0N1oiIHN0cm9rZT0iIzU1NUU3NSIgc3Ryb2tlLXdpZHRoPSIxLjUiLz4KPC9zdmc+',
          scale: "users",
        },
        {
          key: "sources",
          label: "Nombre de sources",
          icon: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHZpZXdCb3g9IjAgMCAyMCAyMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTEgMUMxMC45NDExIDEgMTkgOS4wNTg4NyAxOSAxOU0xIDEwQzUuOTcwNTYgMTAgMTAgMTQuMDI5NCAxMCAxOU0xIDE5SDEuMDUyNzMiIHN0cm9rZT0iIzdCN0Y4OSIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz4KPC9zdmc+',
          scale: "sources",
        },
      ],
    }
  },

  computed: {
    pricingMode() {
      return this.mode
    },

    // Dynamic switch between pricing logics
    pricingCalculator() {
      return this.pricingMode === PRICING_MODES.ADVANCED
        ? usePricingAdvanced()
        : usePricingBasic()
    },

    selectedPlan() {
      return this.pricingCalculator.calculatePlan(this.pricingState)
    },
  },

  methods: {
    onPeriodChange(period) {
      this.pricingState.period = period
    },

    onValueChange(key, value) {
      this.pricingState[key] = value
    },

    // Method to switch pricing mode (for future testing)
    switchPricingMode(newMode) {
      if (Object.values(PRICING_MODES).includes(newMode)) {
        // Adjust initial values according to mode
        const initialValues = INITIAL_VALUES[newMode]
        Object.keys(initialValues).forEach((key) => {
          this.pricingState[key] = initialValues[key]
        })

        this.$emit("mode-change", newMode)
      }
    },
  },

  // Adjust initial values according to mode on mount
  mounted() {
    const initialValues = INITIAL_VALUES[this.pricingMode]
    if (initialValues) {
      Object.keys(initialValues).forEach((key) => {
        this.pricingState[key] = initialValues[key]
      })
    }
  },

  // Watch for mode changes via props
  watch: {
    mode: {
      handler(newMode) {
        const initialValues = INITIAL_VALUES[newMode]
        if (initialValues) {
          Object.keys(initialValues).forEach((key) => {
            this.state[key] = initialValues[key]
          })
        }
      },
      immediate: false,
    },
  },
}
</script>

<style scoped lang="scss">
.container {
  display: flex;
  align-items: center;
  gap: 170px; // Adapted gap for Webflow
  max-width: 1200px;
  width: 100%;
  flex-wrap: wrap;
  justify-content: center;

  @media (max-width: 960px) {
    flex-direction: column;
    gap: 60px; // Adapted gap for Webflow
  }
}

#configurator {
  flex: 1;
  min-width: 300px;

  .header {
    width: fit-content;

    @media (max-width: 960px) {
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
    }

    @media (max-width: 400px) {
      .title {
        font-size: 34px;
      }
      .subtitle {
        font-size: 18px;
      }
    }
  }

  .title {
    color: var(--primary-text-color);
    margin-bottom: 5px;
    margin-top: 0px;
    font-size: 40px;
    font-weight: 700;
  }

  .subtitle {
    color: var(--secondary-text-color);
    margin-top: 0;
    margin-bottom: 40px;
    font-size: 20px;
    font-weight: 400;
  }
}

.sliders-container {
  margin-top: 40px;

  > :not(:last-child) {
    margin-bottom: 40px;
  }
}
</style>
